/**
 * Core type definitions for LinguaNostr language learning platform
 * Supports multiple pedagogical approaches:
 * - Gianfranco Conti's E.P.I. (Extensive Processing Instruction) with MARSEARS
 * - Frequency-based vocabulary acquisition (corpus-derived word lists)
 * - Spaced Repetition System (SM-2 algorithm)
 * - CEFR-aligned curriculum progression
 */

// ── CEFR Levels ──────────────────────────────────────────────────────────

export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export const CEFR_LEVELS: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export const CEFR_DESCRIPTIONS: Record<CEFRLevel, string> = {
  'A1': 'Beginner — Can understand and use familiar everyday expressions',
  'A2': 'Elementary — Can communicate in simple, routine tasks',
  'B1': 'Intermediate — Can deal with most situations while traveling',
  'B2': 'Upper Intermediate — Can interact with fluency and spontaneity',
  'C1': 'Advanced — Can express ideas fluently and precisely',
  'C2': 'Mastery — Can understand virtually everything heard or read',
};

export const CEFR_CEILING: Record<CEFRLevel, number> = {
  'A1': 500,
  'A2': 1000,
  'B1': 2500,
  'B2': 5000,
  'C1': 8000,
  'C2': 15000,
};

// ── Languages ─────────────────────────────────────────────────────────────

export type LanguageCode = 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ja' | 'ko' | 'zh' | 'en';

export interface Language {
  code: LanguageCode;
  name: string;
  nativeName: string;
  flag: string;
  levelsAvailable: CEFRLevel[];
  /** Estimated total vocabulary items available for this language */
  totalVocabulary: number;
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', levelsAvailable: ['A1', 'A2', 'B1', 'B2', 'C1'], totalVocabulary: 8000 },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', levelsAvailable: ['A1', 'A2', 'B1', 'B2', 'C1'], totalVocabulary: 8000 },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', levelsAvailable: ['A1', 'A2', 'B1', 'B2', 'C1'], totalVocabulary: 8000 },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', levelsAvailable: ['A1', 'A2', 'B1', 'B2'], totalVocabulary: 5000 },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', levelsAvailable: ['A1', 'A2', 'B1', 'B2'], totalVocabulary: 5000 },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', levelsAvailable: ['A1', 'A2', 'B1'], totalVocabulary: 3000 },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', levelsAvailable: ['A1', 'A2', 'B1'], totalVocabulary: 3000 },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', levelsAvailable: ['A1', 'A2', 'B1'], totalVocabulary: 3000 },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', levelsAvailable: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'], totalVocabulary: 15000 },
];

// ── Parts of Speech ──────────────────────────────────────────────────────

export type PartOfSpeech =
  | 'noun'
  | 'verb'
  | 'adjective'
  | 'adverb'
  | 'pronoun'
  | 'preposition'
  | 'conjunction'
  | 'interjection'
  | 'determiner'
  | 'phrase'
  | 'chunk';

// ── Vocabulary (Frequency-Based) ─────────────────────────────────────────

export interface VocabularyItem {
  id: string;
  word: string;
  translation: string;
  pronunciation?: string;
  audioUrl?: string;
  partOfSpeech: PartOfSpeech;
  exampleSentences: ExampleSentence[];
  difficulty: number; // 1-5
  tags: string[];
  cefrLevel: CEFRLevel;
  /** Frequency rank — lower is more common (corpus-derived) */
  frequencyRank: number;
  /** Estimated occurrences per million words */
  frequencyPerMillion?: number;
  etymology?: string;
  mnemonic?: string;
  /** Related words for interleaving / elaboration */
  relatedWordIds?: string[];
  /** Image URL for dual coding */
  imageUrl?: string;
}

export interface ExampleSentence {
  sentence: string;
  translation: string;
  audioUrl?: string;
}

// ── Conti's Sentence Builder (Chunk-Based) ──────────────────────────────

/**
 * A Sentence Builder is a structured table of chunks organized by
 * grammatical function (e.g., Subject + Verb + Object + Adverb).
 * Each cell contains multiple options so students can generate
 * combinatorial sentences by picking one item from each column.
 *
 * Example:
 * ┌─────────┬───────────┬──────────────┬──────────┐
 * │ Subject │ Verb      │ Object       │ Adverb   │ ~
 * │ Yo      │ como      │ manzanas     │ mucho    │
 * │ Tú      │ bebes     │ pan          │ siempre  │
 * │ Él      │ come      │ arroz        │ nunca    │
 * └─────────┴───────────┴──────────────┴──────────┘
 */
export interface SentenceBuilder {
  id: string;
  title: string;
  columns: SentenceBuilderColumn[];
  /** Focus chunks — the target language chunks this builder is designed to teach */
  targetChunks: string[];
}

export interface SentenceBuilderColumn {
  name: string;        // e.g. "Subject", "Verb", "Object"
  options: string[];   // e.g. ["Yo", "Tú", "Él", "Ella"]
  translations?: Record<string, string>; // key = option, value = translation
}

// ── Narrow Reading / Listening ──────────────────────────────────────────

/**
 * Narrow input: multiple short texts on the same theme, tightly controlled
 * to maximise target-chunk exposure (Conti's "flooded input").
 */
export interface NarrowText {
  id: string;
  title: string;
  body: string;
  translation: string;
  /** Target chunks embedded in this text (highlighted) */
  targetChunks: string[];
  audioUrl?: string;
}

// ── Conti's MARSEARS Lesson Phases ───────────────────────────────────────

export type MARSEARSPhase =
  | 'modelling'           // M - Sentence Builder / chunk presentation
  | 'awareness_raising'   // A - Grammar/pattern noticing
  | 'receptive_processing' // R - Flooded input, listening/reading tasks
  | 'structured_production' // S - Controlled output, translation drills
  | 'expansion'           // E - Generative grammar, recombination
  | 'autonomy_assessment' // A - Low-stakes assessment
  | 'retrieval_recycling' // R - Interleaved retrieval practice
  | 'spontaneity';        // S - Authentic communicative tasks

// ── Activity Types (Extended for EPI) ────────────────────────────────────

export type ActivityType =
  // Frequency-based activities
  | 'flashcard'
  | 'multiple_choice'
  | 'typing'
  // EPI / Conti activities
  | 'sentence_builder'      // Drag/select chunks to form sentences
  | 'sentence_puzzle'       // Unscramble chunks
  | 'narrow_reading'        // Read target-flooded text
  | 'narrow_listening'      // Listen to target-flooded text
  | 'pattern_substitution'  // Replace one chunk in a sentence
  | 'translation_drill'     // Oral/written translation
  | 'gap_fill_flooded'      // Fill blanks in chunk-flooded text
  | 'reconstruction'        // Reconstruct text from memory
  | 'error_detection'       // Find target-errors
  | 'dictation'             // Write what you hear
  | 'chain_reaction'        // Sequential transformation
  | 'retrieval_grid'        // Word/translation retrieval from grid
  | 'interleaved_review'    // Mixed old + new items
  | 'communicative_task';   // Freer production

// ── Activity Data (Discriminated Union) ──────────────────────────────────

export type ActivityData =
  | FlashcardData
  | MultipleChoiceData
  | TypingData
  | SentenceBuilderData
  | NarrowReadingData
  | PatternSubstitutionData
  | TranslationDrillData
  | GapFillFloodedData
  | ErrorDetectionData
  | ChainReactionData
  | RetrievalGridData
  | InterleavedReviewData;

export interface FlashcardData {
  type: 'flashcard';
  front: string;
  back: string;
  hint?: string;
  imageUrl?: string;
  audioUrl?: string;
}

export interface MultipleChoiceData {
  type: 'multiple_choice';
  question: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
  imageUrl?: string;
  audioUrl?: string;
}

export interface TypingData {
  type: 'typing';
  prompt: string;
  correctAnswer: string;
  acceptableAnswers?: string[];
  hint?: string;
  audioUrl?: string;
}

export interface SentenceBuilderData {
  type: 'sentence_builder';
  columns: SentenceBuilderColumn[];
  /** Correct sentences the builder can form */
  targetSentences: string[];
  /** Language code for correct answer checking */
  languageCode: string;
}

export interface NarrowReadingData {
  type: 'narrow_reading';
  title: string;
  body: string;
  translation: string;
  /** Comprehension questions on the text */
  comprehensionQuestions: MultipleChoiceData[];
  targetChunks: string[];
  /** Audio version for narrow listening */
  audioUrl?: string;
}

export interface PatternSubstitutionData {
  type: 'pattern_substitution';
  sentenceTemplate: string;
  /** The variable/chunk to substitute */
  substitutionSlot: string;
  substitutions: { chunk: string; translation: string }[];
  languageCode: string;
}

export interface TranslationDrillData {
  type: 'translation_drill';
  /** Direction: L1→L2 or L2→L1 */
  direction: 'to_target' | 'from_target';
  items: TranslationItem[];
}

export interface TranslationItem {
  source: string;
  target: string;
  audioUrl?: string;
}

export interface GapFillFloodedData {
  type: 'gap_fill_flooded';
  body: string;                    // Text with ___ gaps
  blanks: GapFillBlank[];
  /** Audio version for dictation variant */
  audioUrl?: string;
}

export interface GapFillBlank {
  id: number;
  correctAnswer: string;
  acceptableAnswers?: string[];
  hint?: string;
}

export interface ErrorDetectionData {
  type: 'error_detection';
  body: string;
  errors: ErrorItem[];
}

export interface ErrorItem {
  /** Location of the error in the body (character index) */
  location: number;
  erroneous: string;
  correction: string;
  explanation: string;
}

export interface ChainReactionData {
  type: 'chain_reaction';
  /** Starting sentence */
  startSentence: string;
  /** Transformations to apply sequentially */
  transformations: TransformationStep[];
  languageCode: string;
}

export interface TransformationStep {
  prompt: string;       // e.g. "Change 'I eat' to 'I ate'"
  answer: string;       // Expected transformed sentence
}

export interface RetrievalGridData {
  type: 'retrieval_grid';
  /** Grid: items to retrieve from memory */
  rows: RetrievalGridRow[];
}

export interface RetrievalGridRow {
  target: string;       // e.g. "Yo como manzanas"
  translation: string;  // e.g. "I eat apples"
  /** Whether to show target first or translation first */
  direction: 't2t' | 't2s';
}

export interface InterleavedReviewData {
  type: 'interleaved_review';
  items: InterleavedReviewItem[];
  /** IDs of the vocabulary items being reviewed */
  vocabularyIds: string[];
  /** IDs of reviewing for SRS progress tracking */
  lessonIds: string[];
}

export interface InterleavedReviewItem {
  question: string;
  correctAnswer: string;
  acceptableAnswers?: string[];
  type: 'translation' | 'recognition' | 'production';
  vocabularyId?: string;
}

// ── Unit / Sub-Unit (Conti Matrix) ──────────────────────────────────────

/**
 * Conti's recycling matrix: each sub-unit introduces new chunks while
 * recycling items from previous sub-units through interleaved retrieval.
 */
export interface SubUnit {
  id: string;
  title: string;
  order: number;
  /** New chunks introduced in this sub-unit */
  newChunkIds: string[];
  /** Chunks recycled from previous sub-units */
  recycledChunkIds: string[];
  /** MARSEARS phases in this sub-unit */
  phases: MARSEARSPhase[];
  /** Activities organized by MARSEARS phase */
  activities: Record<MARSEARSPhase, ActivityData[]>;
  /** Sentence builder(s) for modelling phase */
  sentenceBuilders?: SentenceBuilder[];
  /** Narrow texts for receptive processing */
  narrowTexts?: NarrowText[];
  estimatedMinutes: number;
}

// ── Conti Recycling Matrix (Unit) ───────────────────────────────────────

/**
 * A full Conti unit composed of multiple sub-units that systematically
 * recycle language chunks across the matrix.
 *
 * Sub-unit 1: Focus on new items only (no recycling to avoid interference)
 * Sub-unit 2: Focus on new items with light recycling of sub-unit 1
 * Sub-unit 3: New items + recycling sub-unit 1 & 2
 * Sub-unit 4: New items + recycling sub-unit 1, 2 & 3
 * Sub-unit 5: Fluency/Spontaneity phase — full recycling across all
 */
export interface ContiUnit {
  id: string;
  title: string;
  description: string;
  languageCode: LanguageCode;
  cefrLevel: CEFRLevel;
  subUnits: SubUnit[];
  /** All vocabulary items across the entire unit */
  vocabularyIds: string[];
  /** Target communicative functions */
  functions: string[];
  /** Grammar points covered */
  grammarPoints: GrammarPoint[];
}

// ── Grammar ──────────────────────────────────────────────────────────────

export interface GrammarPoint {
  id: string;
  title: string;
  explanation: string;
  examples: ExampleSentence[];
  rules: string[];
  /** When to introduce in EPI sequence (Expansion phase) */
  introducePhase: 'awareness_raising' | 'expansion';
}

// ── Activity type ↔ ActivityData mapping ─────────────────────────────────

export type Activity = {
  id: string;
  type: ActivityType;
  title: string;
  instructions: string;
  order: number;
  pedagogicalFocus: string[];
} & ActivityData;

// ── SRS Progress ─────────────────────────────────────────────────────────

export interface SRSItemProgress {
  /** Composite key: `${languageCode}:${vocabularyId}` */
  id: string;
  vocabularyId: string;
  languageCode: LanguageCode;
  // SM-2 fields
  easeFactor: number;
  interval: number;
  repetitions: number;
  nextReviewDate: string;   // ISO date
  lastReviewDate?: string;
  // Statistics
  totalReviews: number;
  correctReviews: number;
  // State
  state: 'new' | 'learning' | 'review' | 'mastered';
  // Pedagogy tracking
  lastActivityType?: ActivityType;
}

export interface SessionProgress {
  /** Composite key: `${languageCode}:${unitId}:${subUnitId}` */
  id: string;
  languageCode: LanguageCode;
  unitId: string;
  subUnitId: string;
  /** Completed MARSEARS phases */
  completedPhases: MARSEARSPhase[];
  /** Completed activity IDs */
  completedActivityIds: string[];
  /** Score per phase */
  phaseScores: Record<MARSEARSPhase, number>;
  status: 'not_started' | 'in_progress' | 'completed';
  timeSpent: number; // seconds
  startedAt?: string;
  completedAt?: string;
}

// ── User Master Record ───────────────────────────────────────────────────

export interface UserData {
  activeLanguage: LanguageCode;
  activeCefrLevel: CEFRLevel;
  languageProgress: Record<string, LanguageProgress>;
}

export interface LanguageProgress {
  languageCode: LanguageCode;
  currentLevel: CEFRLevel;
  vocabularyProgress: Record<string, SRSItemProgress>;
  sessionProgress: Record<string, SessionProgress>;
  totalStudyTime: number;
  streak: number;
  lastStudyDate?: string;
  longestStreak: number;
  wordsLearned: number;
  wordsMastered: number;
  totalReviewsCompleted: number;
}

// ── Review Result (SM-2) ─────────────────────────────────────────────────

export interface ReviewResult {
  vocabularyId: string;
  quality: 0 | 1 | 2 | 3 | 4 | 5;
  responseTime: number; // ms
  timestamp: string;
}

export default {};
